/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FunctionComponent, useState } from "react";
import { v4 as uuid } from "uuid";
import NavHeader from "./NavHeader";
import NavDivider from "./NavDivider";
import NavItem from "./NavItem";
import { NavGroupProps, SidebarNavItem } from "@advapacs/common";
import ShowForPermissions from "../ShowForPermissions";
import { isIndividual } from "@src/utils/utils";

const NavGroup: FunctionComponent<NavGroupProps> = (props: NavGroupProps) => {
  const {
    toggleSidebar,
    expandParent,
    navs,
    icon,
    title,
    expandGroup,
    activeGroup,
    selectItem,
    activeItem,
  } = props;
  const [groupActiveGroup, setGroupActiveGroup] = useState("");
  const expandGroupGroup = (id: string) => {
    setGroupActiveGroup(id);
  };
  const [id] = useState(uuid());

  const expand = () => {
    if (expandGroup) {
      expandGroup(id);
    }

    if (expandParent) {
      expandParent();
    }
  };

  const toggle = () => {
    if (expandGroup) {
      expandGroup(id === activeGroup ? "" : id);
    }
  };

  return (
    <li
      className={`nav-item nav-item-submenu ${
        activeGroup && activeGroup === id
          ? "nav-item-expanded nav-item-open"
          : ""
      }`}
    >
      <a aria-hidden onClick={toggle} className="nav-link">
        {icon && <i className={icon} />}
        <span>{title}</span>
      </a>
      <ul className="nav nav-group-sub" data-submenu-title={title}>
        {navs?.map((item: SidebarNavItem) => {
          if (item.type === "item" && item.show) {
            return (
              <ShowForPermissions
                key={item?.title}
                can={isIndividual() ? undefined : item?.can}
                allowVisibleToOwner={item?.allowVisibleToOwner}
              >
                <NavItem
                  onLinkClick={() => toggleSidebar && toggleSidebar()}
                  activeItem={activeItem}
                  selectItem={selectItem}
                  expandParent={expand}
                  {...item}
                />
              </ShowForPermissions>
            );
          }

          if (item.type === "group" && item.show) {
            return (
              <ShowForPermissions
                can={isIndividual() ? undefined : item?.can}
                allowVisibleToOwner={item?.allowVisibleToOwner}
              >
                <NavGroup
                  key={item?.title}
                  selectItem={selectItem}
                  activeItem={activeItem}
                  expandGroup={expandGroupGroup}
                  expandParent={expand}
                  navs={item?.navs}
                  type={item?.type}
                  href={item?.href}
                  icon={item?.icon}
                  title={item?.title}
                  activeGroup={groupActiveGroup}
                />
              </ShowForPermissions>
            );
          }

          if (item.type === "header") {
            return <NavHeader key={uuid()} {...item} />;
          }

          if (item.type === "divider") {
            return <NavDivider key={uuid()} />;
          }

          return null;
        })}
      </ul>
    </li>
  );
};

export default NavGroup;
